import { EllipsisVerticalIcon } from 'lucide-react';
import * as React from 'react';

import { DimeDropdownMenu, DimeDropdownMenuTrigger } from '../DimeDropdownMenu.js';
import { DimeSeparator } from '../DimeSeparator.js';
import { isToolbarItemAllowed } from '../lib/is-toolbar-item-allowed.js';
import { NOT_AUTHORIZED_MESSAGE } from '../lib/not-authorized-message.js';
import { toolbarDisabledTooltip } from '../lib/toolbar-disabled-tooltip.js';
import { cn } from '../lib/utils.js';
import type {
  DimeActionToolbarAction,
  DimeActionToolbarMenuItem,
} from './DimeActionToolbar.types.js';
import { DimeActionToolbarButton } from './DimeActionToolbarButton.js';
import { DimeActionToolbarMenuBody } from './DimeActionToolbarMenuBody.js';
import { DimeActionToolbarMenuContent } from './DimeActionToolbarMenuContent.js';
import { DimeActionToolbarMenuItems } from './DimeActionToolbarMenuItems.js';

type DimeActionToolbarProps = Readonly<{
  actions: DimeActionToolbarAction[];
  moreItems?: DimeActionToolbarMenuItem[];
  /** Folds secondary plain actions into More and lets the bar scroll; labels stay, since touch has no hover. */
  compact?: boolean;
  leftRail?: React.ReactNode;
  rightRail?: React.ReactNode;
  'aria-label'?: string;
  className?: string;
}>;

const isFoldable = (action: DimeActionToolbarAction) =>
  action.priority === 'secondary' && !action.menu;

function toMenuItem(action: DimeActionToolbarAction): DimeActionToolbarMenuItem {
  return {
    key: action.key,
    label: action.label,
    icon: action.icon,
    onSelect: action.onSelect,
    allowed: action.allowed,
    deniedMessage: action.deniedMessage,
    disabled: action.disabled,
    disabledReason: action.disabledReason,
  };
}

function ToolbarSeparator() {
  return <DimeSeparator orientation="vertical" className="mx-2 my-1 h-auto" />;
}

function ToolbarAction({ action }: Readonly<{ action: DimeActionToolbarAction }>) {
  const { menu } = action;
  // An empty menu has nothing to deny, so its disabled reason (e.g. nothing on file) is shown instead of a permission message.
  const permitted = menu
    ? menu.length === 0 || menu.some(isToolbarItemAllowed)
    : isToolbarItemAllowed(action);
  const blocked = Boolean(action.disabled) || !permitted;
  const tooltip = toolbarDisabledTooltip({
    permitted,
    disabled: action.disabled,
    disabledReason: action.disabledReason,
    deniedMessage: action.deniedMessage,
  });
  const button = (
    <DimeActionToolbarButton
      icon={action.icon}
      label={action.label}
      tone={action.tone}
      hasMenu={Boolean(menu)}
      disabled={blocked}
      disabledTooltip={blocked ? tooltip : undefined}
      onClick={action.onSelect}
    />
  );
  if (!menu || blocked) return button;
  return (
    <DimeDropdownMenu modal={false}>
      <DimeDropdownMenuTrigger asChild>{button}</DimeDropdownMenuTrigger>
      <DimeActionToolbarMenuBody
        items={menu}
        label={action.menuLabel}
        search={action.menuSearch}
        align="start"
        className={
          action.menuSize === 'wide'
            ? 'min-w-72 max-w-(--radix-dropdown-menu-content-available-width)'
            : undefined
        }
      />
    </DimeDropdownMenu>
  );
}

export function DimeActionToolbar({
  actions,
  moreItems = [],
  compact = false,
  leftRail,
  rightRail,
  'aria-label': ariaLabel = 'Page Actions',
  className,
}: DimeActionToolbarProps) {
  const visible = compact ? actions.filter((action) => !isFoldable(action)) : actions;
  const folded = compact ? actions.filter(isFoldable).map(toMenuItem) : [];
  const overflow = [
    ...folded,
    ...moreItems.map((item, index) =>
      index === 0 && folded.length > 0 ? { ...item, separatorBefore: true } : item,
    ),
  ];
  const overflowAllowed = overflow.some(isToolbarItemAllowed);

  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      className={cn(
        'flex shrink-0 items-stretch gap-0.5 border-b px-2 py-1',
        compact && 'overflow-x-auto',
        className,
      )}
    >
      {leftRail ? (
        <>
          {leftRail}
          <ToolbarSeparator />
        </>
      ) : null}
      {visible.map((action) => (
        <ToolbarAction key={action.key} action={action} />
      ))}
      {overflow.length > 0 ? (
        <>
          <ToolbarSeparator />
          <DimeDropdownMenu>
            <DimeDropdownMenuTrigger asChild>
              <DimeActionToolbarButton
                icon={<EllipsisVerticalIcon />}
                label="More"
                disabled={!overflowAllowed}
                disabledTooltip={overflowAllowed ? undefined : NOT_AUTHORIZED_MESSAGE}
              />
            </DimeDropdownMenuTrigger>
            <DimeActionToolbarMenuContent align="end" className="min-w-52">
              <DimeActionToolbarMenuItems items={overflow} />
            </DimeActionToolbarMenuContent>
          </DimeDropdownMenu>
        </>
      ) : null}
      {rightRail ? (
        <div className="ml-auto flex items-stretch">
          <ToolbarSeparator />
          {rightRail}
        </div>
      ) : null}
    </div>
  );
}
